import { useState } from "react";
import { Share2, ChevronDown, ChevronRight, CheckCircle2, ShieldCheck, Sparkles, ExternalLink, AlertCircle } from "lucide-react";

const PROV_STYLE = {
  VERIFIED: "bg-emerald-50 text-emerald-700 border-emerald-200",
  USER_PROVIDED: "bg-indigo-50 text-indigo-700 border-indigo-200",
  AI_ASSISTED: "bg-violet-50 text-violet-700 border-violet-200",
  CALCULATED: "bg-sky-50 text-sky-700 border-sky-200",
  ESTIMATE: "bg-amber-50 text-amber-700 border-amber-200",
  UNAVAILABLE: "bg-slate-100 text-slate-500 border-slate-200",
};

const VERIFY_STYLE = {
  confirmed: "bg-emerald-50 text-emerald-700 border-emerald-200",
  suspected: "bg-amber-50 text-amber-700 border-amber-200",
  unverified: "bg-slate-100 text-slate-500 border-slate-200",
};

function scoreColor(n) {
  if (n == null) return "text-slate-400";
  if (n >= 70) return "text-emerald-600";
  if (n >= 40) return "text-amber-600";
  return "text-rose-600";
}
function barColor(n) {
  if (n == null) return "bg-slate-200";
  if (n >= 70) return "bg-emerald-500";
  if (n >= 40) return "bg-amber-500";
  return "bg-rose-500";
}

function Chip({ label, cls }) {
  return <span className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide ${cls}`}>{label}</span>;
}

function AccountCard({ a, idx }) {
  const [open, setOpen] = useState(idx === 0);
  const acc = a.account || {};
  const scored = a.status === "scored";
  return (
    <div className="rounded-xl border border-slate-200 overflow-hidden" data-testid={`social-card-${idx}`}>
      <button type="button" onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center gap-3 p-4 text-left hover:bg-slate-50 transition-colors">
        {open ? <ChevronDown className="w-4 h-4 text-slate-400 shrink-0" /> : <ChevronRight className="w-4 h-4 text-slate-400 shrink-0" />}
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-semibold text-slate-900">{acc.platform_label || acc.platform}</span>
            {acc.handle && <span className="text-sm text-slate-500 truncate">@{acc.handle.replace(/^@/, "")}</span>}
            <Chip label={a.verification_status} cls={VERIFY_STYLE[a.verification_status] || VERIFY_STYLE.unverified} />
            {acc.on_website && <Chip label="On website" cls="bg-sky-50 text-sky-700 border-sky-200" />}
          </div>
        </div>
        <div className="text-right shrink-0">
          {scored ? (
            <div className={`font-heading text-2xl font-extrabold ${scoreColor(a.overall)}`}>{a.overall}<span className="text-sm text-slate-400">/100</span></div>
          ) : (
            <div className="text-[11px] font-semibold text-slate-500 max-w-[130px]">Insufficient data</div>
          )}
          <div className="text-[10px] text-slate-400">{a.coverage_pct}% coverage · {a.confidence}</div>
        </div>
      </button>

      {open && (
        <div className="px-4 pb-4 border-t border-slate-100">
          {a.present === false && (
            <p className="mt-3 text-sm text-rose-600 flex items-center gap-1.5"><AlertCircle className="w-4 h-4" /> This profile URL returned "not found".</p>
          )}
          {/* Category breakdown */}
          <div className="mt-4 space-y-2.5">
            {(a.categories || []).map((c) => (
              <div key={c.key} data-testid={`social-cat-${idx}-${c.key}`}>
                <div className="flex items-center justify-between text-[12px] mb-1">
                  <span className="text-slate-700">{c.label} <span className="text-slate-400">· {c.weight}%</span></span>
                  <span className={`font-semibold ${c.available ? scoreColor(c.score) : "text-slate-400"}`}>
                    {c.available ? `${c.score}` : "No data"}
                  </span>
                </div>
                <div className="h-1.5 rounded-full bg-slate-100 overflow-hidden">
                  <div className={`h-full ${c.available ? barColor(c.score) : "bg-slate-200"}`} style={{ width: c.available ? `${c.score}%` : "100%", opacity: c.available ? 1 : 0.3 }} />
                </div>
                {(c.provenance || []).length > 0 && (
                  <div className="flex flex-wrap gap-1 mt-1">
                    {c.provenance.map((p, i) => <Chip key={i} label={p} cls={PROV_STYLE[p] || PROV_STYLE.UNAVAILABLE} />)}
                  </div>
                )}
              </div>
            ))}
          </div>

          {/* AI-assisted qualitative notes */}
          {a.ai_assessment && (a.ai_assessment.observations?.length || a.ai_assessment.recommendations?.length) ? (
            <div className="mt-4 rounded-lg bg-violet-50/60 border border-violet-100 p-3">
              <div className="flex items-center gap-1.5 mb-1.5"><Sparkles className="w-3.5 h-3.5 text-violet-600" /><span className="text-[11px] font-semibold text-violet-700 uppercase tracking-wide">AI-assisted assessment</span></div>
              {(a.ai_assessment.observations || []).map((o, i) => <p key={`o${i}`} className="text-[13px] text-slate-700">• {o}</p>)}
              {(a.ai_assessment.recommendations || []).map((rc, i) => <p key={`r${i}`} className="text-[13px] text-slate-700 mt-0.5">→ {rc}</p>)}
            </div>
          ) : null}

          {/* Authorized analytics status (honest UNAVAILABLE) */}
          {a.authorized_api && a.authorized_api.status === "NOT_CONFIGURED" && (
            <p className="mt-3 text-[11px] text-slate-400">Live analytics: {a.authorized_api.detail}</p>
          )}
          {acc.url && (
            <a href={acc.url} target="_blank" rel="noreferrer" className="mt-3 inline-flex items-center gap-1 text-[12px] font-medium text-indigo-600 hover:text-indigo-700">
              <ExternalLink className="w-3.5 h-3.5" /> View profile
            </a>
          )}
        </div>
      )}
    </div>
  );
}

export function SocialIntelligence({ social }) {
  if (!social || !social.enabled) return null;
  const s = social.summary || {};
  const accounts = social.accounts || [];
  const scored = s.status === "scored";

  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-6 sm:p-8 shadow-sm" data-testid="social-intelligence">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="flex items-center gap-3">
          <span className="grid place-items-center w-10 h-10 rounded-xl bg-indigo-600 text-white"><Share2 className="w-5 h-5" /></span>
          <div>
            <h3 className="font-heading text-xl font-bold text-slate-900">Social Media Intelligence</h3>
            <p className="text-[12px] text-slate-500">Deterministic scoring v{social.model_version} · categories without evidence are excluded, never scored zero.</p>
          </div>
        </div>
        <div className="text-right">
          {scored ? (
            <div className={`font-heading text-4xl font-extrabold ${scoreColor(s.overall)}`} data-testid="social-overall-score">{s.overall}<span className="text-lg text-slate-400">/100</span></div>
          ) : (
            <div className="text-sm font-semibold text-slate-500 max-w-[180px]" data-testid="social-overall-score">Insufficient data for a reliable score</div>
          )}
          <div className="text-[11px] text-slate-400">{s.scored_accounts_count}/{s.accounts_count} accounts scored · {s.confidence} confidence</div>
        </div>
      </div>

      {/* Summary chips */}
      <div className="mt-4 flex flex-wrap gap-2 text-[12px]">
        {(s.platforms_present || []).map((p) => (
          <span key={p} className="inline-flex items-center gap-1 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200 px-2.5 py-1"><CheckCircle2 className="w-3 h-3" /> {p}</span>
        ))}
        {(s.coverage_gaps || []).map((g) => (
          <span key={g} className="inline-flex items-center gap-1 rounded-full bg-slate-100 text-slate-500 border border-slate-200 px-2.5 py-1">Missing: {g}</span>
        ))}
        {s.brand_consistent === false && (
          <span className="inline-flex items-center gap-1 rounded-full bg-amber-50 text-amber-700 border border-amber-200 px-2.5 py-1">Inconsistent branding</span>
        )}
      </div>
      {scored && <p className="mt-2 text-[11px] text-slate-400">{s.aggregation_method}</p>}

      {/* Account scorecards */}
      {accounts.length > 0 ? (
        <div className="mt-5 space-y-3">
          {accounts.map((a, i) => <AccountCard key={i} a={a} idx={i} />)}
        </div>
      ) : (
        <p className="mt-5 text-sm text-slate-600">No social accounts were provided or detected. See the recommendation below to get set up on the right platforms.</p>
      )}

      {/* Provenance legend */}
      <div className="mt-5 flex items-start gap-2 rounded-lg bg-slate-50 border border-slate-200 p-3">
        <ShieldCheck className="w-4 h-4 text-slate-400 shrink-0 mt-0.5" />
        <p className="text-[11px] text-slate-500">
          <span className="font-semibold text-emerald-700">VERIFIED</span> confirmed independently ·
          <span className="font-semibold text-indigo-700"> USER_PROVIDED</span> from the owner ·
          <span className="font-semibold text-violet-700"> AI_ASSISTED</span> interpretation ·
          <span className="font-semibold text-slate-500"> UNAVAILABLE</span> not accessible without an authorized API.
          Followers, engagement and reach are never fabricated.
        </p>
      </div>
    </div>
  );
}

export default SocialIntelligence;
